import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, AlertTriangle, PlusCircle, LogOut,
  Settings, FileText, Activity, ClipboardList, Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Operator Sidebar - Only shows operator-specific navigation
 * NO parent routes visible
 */
export const OperatorSidebar = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { to: '/operator', icon: LayoutDashboard, label: 'Overview', exact: true },
    { to: '/operator/families', icon: Users, label: 'Families' },
    { to: '/operator/queue', icon: AlertTriangle, label: 'Queue' },
    { to: '/operator/reliability', icon: Activity, label: 'Reliability' },
    { to: '/operator/execution-logs', icon: ClipboardList, label: 'Execution Logs' },
  ];

  const NavItem = ({ to, icon: Icon, label, exact = false }) => (
    <NavLink
      to={to}
      end={exact}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-all ${
          isActive
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100'
        }`
      }
    >
      <Icon className="w-5 h-5" />
      {label}
    </NavLink>
  );

  const handleNewFamily = () => {
    navigate('/operator/new-family');
  };

  return (
    <div className="w-64 bg-white border-r border-slate-200 h-screen sticky top-0 flex flex-col" data-testid="operator-sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <h1 className="text-2xl font-bold text-indigo-600 tracking-tight">Kivo</h1>
        <p className="text-xs text-slate-500 mt-1">Operator Dashboard</p>
      </div>
      
      <nav className="flex-1 p-4 space-y-2">
        {/* New Family Button */}
        <button
          onClick={handleNewFamily}
          data-testid="nav-new-family"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium w-full bg-green-50 text-green-700 hover:bg-green-100 transition-all"
        >
          <PlusCircle className="w-5 h-5" />
          New Family
        </button>

        <div className="pt-2 space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </div>

        {/* Debug section */}
        <div className="pt-6 border-t border-slate-200 mt-6">
          <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Debug
          </p>
          <NavLink
            to="/operator/debug"
            className="flex items-center gap-3 px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-50 text-sm"
          >
            <FileText className="w-4 h-4" />
            System Status
          </NavLink>
          <NavLink
            to="/operator/test-data-cleanup"
            className="flex items-center gap-3 px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-50 text-sm"
            data-testid="nav-test-data-cleanup"
          >
            <Trash2 className="w-4 h-4" />
            Cleanup Test Data
          </NavLink>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <button
          onClick={logout}
          className="flex items-center gap-2 px-4 py-2 w-full text-slate-500 hover:bg-slate-50 rounded-lg text-sm"
          data-testid="operator-logout"
        >
          <LogOut className="w-4 h-4" />
          Switch Role
        </button>
        <p className="text-xs text-slate-400 text-center mt-2">Kivo v2.0</p>
      </div>
    </div>
  );
};

export default OperatorSidebar;
